import { Division, Select, Span, type SelectOption } from "@ir/ui";
import { useMemo } from "react";
import type { MasterDataReasonCodeMappingFilters } from "@src/presentation/providers/MasterDataReasonCodeMappingsDataProvider";
import type {
  DeductionTypeSelectGroup,
  PromoTypeSelectOption,
  PurchaserSelectOption,
} from "./MasterDataReasonCodeMappingAddEditForm";
import {
  buildDeductionTypeSelectOptions,
  DEDUCTION_TYPE_SELECT_CLASS,
  findLeafOptionByValue,
  formatDeductionTypeGroupLabel,
} from "./deductionTypeSelectOptions";

export type ReasonCodeMappingFilterDraft = MasterDataReasonCodeMappingFilters;

interface MasterDataReasonCodeMappingFilterFieldsProps {
  draft: ReasonCodeMappingFilterDraft;
  onChange: (next: ReasonCodeMappingFilterDraft) => void;
  purchaserOptions: PurchaserSelectOption[];
  purchasersLoading?: boolean;
  deductionTypeGroups: DeductionTypeSelectGroup[];
  deductionTypesLoading?: boolean;
  promoTypeOptions: PromoTypeSelectOption[];
  promoTypesLoading?: boolean;
}

function parseIdFromSelect(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === "object" && "value" in (value as object)) {
    const raw = (value as SelectOption).value;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

function findOptionByValue(
  options: SelectOption[],
  value: number | null,
): SelectOption | undefined {
  const raw = value == null ? null : String(value);
  return options.find((option) => option.value === raw);
}

export function MasterDataReasonCodeMappingFilterFields({
  draft,
  onChange,
  purchaserOptions,
  purchasersLoading = false,
  deductionTypeGroups,
  deductionTypesLoading = false,
  promoTypeOptions,
  promoTypesLoading = false,
}: Readonly<MasterDataReasonCodeMappingFilterFieldsProps>) {
  const purchaserSelectOptions = useMemo<SelectOption[]>(
    () =>
      purchaserOptions.map((option) => ({
        label: option.purchaserName,
        value: String(option.purchaserId),
      })),
    [purchaserOptions],
  );

  const deductionTypeSelectOptions = useMemo(
    () => buildDeductionTypeSelectOptions(deductionTypeGroups),
    [deductionTypeGroups],
  );

  const promoTypeSelectOptions = useMemo<SelectOption[]>(
    () =>
      promoTypeOptions.map((option) => ({
        label: option.promoTypeName,
        value: String(option.promoTypeId),
      })),
    [promoTypeOptions],
  );

  return (
    <>
      <Division>
        <Span className="list-filter-drawer__field-label">Purchaser</Span>
        <Select
          key={`filter-purchaser-${draft.purchaserId ?? "none"}-${purchaserSelectOptions.length}`}
          size="medium"
          isSearchable
          isClearable
          isLoading={purchasersLoading}
          placeholder="Select purchaser"
          options={purchaserSelectOptions}
          defaultValue={findOptionByValue(
            purchaserSelectOptions,
            draft.purchaserId,
          )}
          onChange={(next) =>
            onChange({ ...draft, purchaserId: parseIdFromSelect(next) })
          }
          aria-label="Filter by purchaser"
        />
      </Division>

      <Division>
        <Span className="list-filter-drawer__field-label">Deduction Type</Span>
        <Select
          key={`filter-deduction-${draft.deductionTypeId ?? "none"}-${deductionTypeSelectOptions.length}`}
          className={DEDUCTION_TYPE_SELECT_CLASS}
          size="medium"
          isSearchable
          isClearable
          isLoading={deductionTypesLoading}
          placeholder="Select deduction type"
          options={deductionTypeSelectOptions}
          customGroupLabel={formatDeductionTypeGroupLabel}
          defaultValue={findLeafOptionByValue(
            deductionTypeSelectOptions,
            draft.deductionTypeId,
          )}
          onChange={(next) =>
            onChange({ ...draft, deductionTypeId: parseIdFromSelect(next) })
          }
          aria-label="Filter by deduction type"
        />
      </Division>

      <Division>
        <Span className="list-filter-drawer__field-label">Promo Type</Span>
        <Select
          key={`filter-promo-${draft.promoTypeId ?? "none"}-${promoTypeSelectOptions.length}`}
          size="medium"
          isSearchable
          isClearable
          isLoading={promoTypesLoading}
          placeholder="Select promo type"
          options={promoTypeSelectOptions}
          defaultValue={findOptionByValue(
            promoTypeSelectOptions,
            draft.promoTypeId,
          )}
          onChange={(next) =>
            onChange({ ...draft, promoTypeId: parseIdFromSelect(next) })
          }
          aria-label="Filter by promo type"
        />
      </Division>
    </>
  );
}
